import { getUrlFriendlyString, hasValue, simpleHash } from '@utils';
const CACHE_PREFIX = 'lucy_cache_';
const DEFAULT_CACHE_TIME = 5; // 5 minutes
export class CacheManager {
    static getInstance() {
        if (!CacheManager.instance) {
            CacheManager.instance = new CacheManager();
        }
        return CacheManager.instance;
    }
    /**
     * Generate cache key from action/service parameters
     */
    generateCacheKey(type, identifier, params) {
        const paramsHash = this.hashObject(params);
        return getUrlFriendlyString(`${type}_${identifier}_${paramsHash}`);
    }
    /**
     * Create a simple hash from object for cache key
     */
    hashObject(obj) {
        const str = JSON.stringify(obj, Object.keys(obj).sort());
        return simpleHash(str, null, { base: 36, ignorePrefix: true });
    }
    /**
     * Get cached data if available and not expired
     */
    getCachedData(cacheKey) {
        try {
            const cachedItem = localStorage.getItem(CACHE_PREFIX + cacheKey);
            if (!cachedItem)
                return null;
            const parsed = JSON.parse(cachedItem);
            const now = Date.now();
            if (now > parsed.expiresAt) {
                // Cache expired, remove it
                this.removeCachedData(cacheKey);
                return null;
            }
            return parsed.data;
        }
        catch (error) {
            console.warn('Error reading from cache:', error);
            return null;
        }
    }
    /**
     * Store data in cache
     */
    setCachedData(cacheKey, data, cacheTimeMinutes = DEFAULT_CACHE_TIME) {
        try {
            const now = Date.now();
            const expiresAt = now + (cacheTimeMinutes * 60 * 1000); // Convert minutes to milliseconds
            const cachedItem = {
                data,
                timestamp: now,
                expiresAt
            };
            localStorage.setItem(CACHE_PREFIX + cacheKey, JSON.stringify(cachedItem));
        }
        catch (error) {
            console.warn('Error storing in cache:', error);
        }
    }
    /**
     * Remove specific cached data
     */
    removeCachedData(cacheKey) {
        try {
            localStorage.removeItem(CACHE_PREFIX + cacheKey);
        }
        catch (error) {
            console.warn('Error removing from cache:', error);
        }
    }
    /**
     * Clear all Lucy cache data
     */
    clearAllCache() {
        try {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith(CACHE_PREFIX)) {
                    localStorage.removeItem(key);
                }
            });
        }
        catch (error) {
            console.warn('Error clearing cache:', error);
        }
    }
    /**
     * Clean up expired cache entries
     */
    cleanupExpiredCache() {
        try {
            const keys = Object.keys(localStorage);
            const now = Date.now();
            keys.forEach(key => {
                if (key.startsWith(CACHE_PREFIX)) {
                    try {
                        const cachedItem = JSON.parse(localStorage.getItem(key) || '');
                        if (now > cachedItem.expiresAt) {
                            localStorage.removeItem(key);
                        }
                    }
                    catch (_a) {
                        // If parsing fails, remove the corrupted cache entry
                        localStorage.removeItem(key);
                    }
                }
            });
        }
        catch (error) {
            console.warn('Error cleaning up cache:', error);
        }
    }
    /**
     * Get cache statistics
     */
    getCacheStats() {
        try {
            const keys = Object.keys(localStorage);
            const cacheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
            let totalSize = 0;
            cacheKeys.forEach(key => {
                const item = localStorage.getItem(key);
                if (item) {
                    totalSize += item.length;
                }
            });
            return {
                totalEntries: cacheKeys.length,
                totalSize
            };
        }
        catch (error) {
            console.warn('Error getting cache stats:', error);
            return { totalEntries: 0, totalSize: 0 };
        }
    }
}
/**
 * Utility function to check if caching should be used
 */
export function shouldUseCache(options) {
    return (options === null || options === void 0 ? void 0 : options.enableCache) === true;
}
/**
 * Get cache time in minutes, with fallback to default
 */
export function getCacheTime(options) {
    return hasValue(options === null || options === void 0 ? void 0 : options.cacheTime, true, true) ? options.cacheTime : DEFAULT_CACHE_TIME;
}
/**
 * Get or generate cache key
 */
export function resolveCacheKey(options, type, identifier, params) {
    const cacheManager = CacheManager.getInstance();
    if (hasValue(options === null || options === void 0 ? void 0 : options.cacheKey)) {
        return options.cacheKey;
    }
    return cacheManager.generateCacheKey(type, identifier, params);
}
